import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  PatientTable,
  Button,
  AppSidebar,
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  Separator,
  EnhancedPatientInformationDialog,
  SupportFeedbackDialog,
  AdminAccountDialog,
  type Patient
} from '@joinomu/ui'
import { useAuth } from '@/hooks/useAuth'
import { authService, emailService } from '@joinomu/shared'

export function AdminPatientsPage() {
  const { user, userRole } = useAuth()
  const navigate = useNavigate()
  const [patients, setPatients] = useState<Patient[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [patientPreferences, setPatientPreferences] = useState<any[]>([])
  const [patientOrders, setPatientOrders] = useState<any[]>([])
  const [patientAppointments, setPatientAppointments] = useState<any[]>([])
  const [providers, setProviders] = useState<any[]>([])
  
  // Support/Feedback dialog state
  const [supportFeedbackDialogOpen, setSupportFeedbackDialogOpen] = useState(false)
  const [supportFeedbackType, setSupportFeedbackType] = useState<'support' | 'feedback'>('support')
  const [accountDialogOpen, setAccountDialogOpen] = useState(false)

  useEffect(() => {
    // Redirect if not admin
    if (userRole && userRole !== 'admin') {
      navigate('/')
      return
    }

    if (userRole === 'admin') {
      fetchPatients()
    }
  }, [userRole, navigate])

  const fetchPatients = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const { data, error } = await authService.getAllPatients()
      
      if (error) {
        setError('Failed to load patients: ' + error.message)
        console.error('Error fetching patients:', error)
      } else {
        // The getAllPatients service now includes appointment data, no need for individual fetching
        console.log('Received patients data with appointments from optimized service:', data)
        setPatients(data)
      }
    } catch (err) {
      setError('An unexpected error occurred while loading patients')
      console.error('Exception fetching patients:', err)
    } finally {
      setLoading(false)
    }
  }

  // Fetch patient medication preferences from database
  const fetchPatientPreferences = async (profileId: string) => {
    try {
      const { data, error } = await authService.getPatientMedicationPreferences(profileId)
      
      if (error) {
        console.error('Error fetching medication preferences:', error)
        return []
      }

      return data
    } catch (error) {
      console.error('Exception fetching medication preferences:', error)
      return []
    }
  }

  // Fetch patient medication orders from database
  const fetchPatientOrders = async (patientId: string) => {
    try {
      const { data, error } = await authService.getPatientMedicationOrders(patientId)
      
      if (error) {
        console.error('Error fetching medication orders:', error)
        return []
      }

      return data
    } catch (error) {
      console.error('Exception fetching medication orders:', error)
      return []
    }
  }

  // Fetch patient appointments from database
  const fetchPatientAppointments = async (profileId: string) => {
    try {
      const { data, error } = await authService.getPatientAppointments(profileId, true)
      
      if (error) {
        console.error('Error fetching patient appointments:', error)
        return []
      }

      return data
    } catch (error) {
      console.error('Exception fetching patient appointments:', error)
      return []
    }
  }

  // Fetch all providers for reschedule functionality
  const fetchProviders = async () => {
    try {
      const { data, error } = await authService.getAllProviders()
      
      if (error) {
        console.error('Error fetching providers:', error)
        return []
      }

      return data?.map((provider: any) => ({
        id: provider.id,
        name: `${provider.first_name} ${provider.last_name}`,
        specialty: provider.specialty,
        profile_id: provider.profile_id
      })) || []
    } catch (error) {
      console.error('Exception fetching providers:', error)
      return []
    }
  }

  const handlePatientClick = async (patient: Patient) => {
    console.log('Patient clicked:', patient)
    setSelectedPatient(patient)
    setDialogOpen(true)

    // Fetch medication preferences and orders
    try {
      const preferences = await fetchPatientPreferences((patient as any).profile_id || patient.id)
      setPatientPreferences(preferences)
      console.log('Loaded patient preferences:', preferences)
    } catch (error) {
      console.error('Error loading medication preferences:', error)
      setPatientPreferences([])
    }

    try {
      const orders = await fetchPatientOrders(patient.id)
      setPatientOrders(orders)
      console.log('Loaded patient orders:', orders)
    } catch (error) {
      console.error('Error loading medication orders:', error)
      setPatientOrders([])
    }

    // Fetch patient appointments
    try {
      const appointments = await fetchPatientAppointments(patient.profile_id)
      setPatientAppointments(appointments)
      console.log('Loaded patient appointments:', appointments)
    } catch (error) {
      console.error('Error loading patient appointments:', error)
      setPatientAppointments([])
    }

    // Fetch providers for reschedule functionality
    try {
      const providersList = await fetchProviders()
      setProviders(providersList)
      console.log('Loaded providers for reschedule:', providersList)
    } catch (error) {
      console.error('Error loading providers:', error)
      setProviders([])
    }
  }

  const handleAddPatient = () => {
    console.log('Add new patient')
    // TODO: Navigate to add patient page
    // navigate('/admin/patients/new')
  }

  const handleSignOut = async () => {
    await authService.signOut()
    navigate('/')
  }

  const handleAccountClick = () => {
    setAccountDialogOpen(true)
  }

  const handleSupportFeedbackClick = (type: "Support" | "Feedback") => {
    console.log('🎯 PATIENTS PAGE Admin handleSupportFeedbackClick called with type:', type)
    console.log('🎯 PATIENTS PAGE Admin current supportFeedbackDialogOpen state:', supportFeedbackDialogOpen)
    console.log('🎯 PATIENTS PAGE Admin current supportFeedbackType:', supportFeedbackType)
    setSupportFeedbackType(type.toLowerCase() as 'support' | 'feedback')
    setSupportFeedbackDialogOpen(true)
    console.log('🎯 PATIENTS PAGE Admin set supportFeedbackDialogOpen to true')
    console.log('🎯 PATIENTS PAGE Admin function execution complete')
  }

  const handleSupportFeedbackSubmit = async (message: string, type: 'support' | 'feedback') => {
    if (!user?.email) {
      return { 
        success: false, 
        error: 'User email not available' 
      }
    }

    try {
      const result = await emailService.sendSupportFeedback({
        type,
        message,
        userEmail: user.email,
        userName: user.user_metadata?.full_name || user.email.split('@')[0] || 'Anonymous Admin'
      })

      return result
    } catch (error) {
      console.error('Failed to send support/feedback message:', error)
      return {
        success: false,
        error: 'Failed to send message'
      }
    }
  }

  const handleRescheduleAppointment = async (appointmentData: {
    appointmentId: string
    appointmentDate: string
    startTime: string
  }) => {
    try {
      const result = await authService.rescheduleAppointment(
        appointmentData.appointmentId,
        appointmentData.appointmentDate,
        appointmentData.startTime,
        'admin',
        user?.id || '',
        'Rescheduled by admin'
      )
      
      if (result.success) {
        // Refresh appointments data
        if (selectedPatient) {
          const appointments = await fetchPatientAppointments(selectedPatient.profile_id)
          setPatientAppointments(appointments)
        }
      }
      
      return result
    } catch (error) {
      console.error('Error rescheduling appointment:', error)
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Failed to reschedule appointment'
      }
    }
  }

  const handleGetAvailableSlots = async (
    providerId: string,
    startDate: string,
    endDate: string,
    treatmentType?: string
  ) => {
    try {
      const result = await authService.getAvailableSlots(providerId, startDate, endDate, treatmentType)
      return result
    } catch (error) {
      console.error('Error getting available slots:', error)
      return { data: [], error }
    }
  }

  if (userRole && userRole !== 'admin') {
    return null // Will redirect
  }

  const userData = {
    name: user?.email?.split('@')[0] || 'Admin',
    email: user?.email || 'admin@example.com',
    avatar: ''
  }

  return (
    <SidebarProvider>
      <AppSidebar 
        user={userData}
        onLogout={handleSignOut}
        userRole="admin"
        onNavigate={(item: string) => {
          if (item === 'Dashboard') navigate('/admin/dashboard')
          else if (item === 'Patients') navigate('/admin/patients')
          else if (item === 'Providers') navigate('/admin/providers')
          else if (item === 'Messaging') navigate('/admin/messaging')
        }}
        onAccountClick={handleAccountClick}
        onSupportFeedbackClick={handleSupportFeedbackClick}
      />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>Patient Management</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>
        <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
            {error && (
              <div className="border border-destructive/20 bg-destructive/10 rounded-lg p-4 mx-6 mt-6">
                <p className="text-destructive">{error}</p>
                <Button onClick={fetchPatients} className="mt-4">
                  Try Again
                </Button>
              </div>
            )}
            
            <PatientTable
              title="Patient Management"
              subtitle={loading ? "Loading patients..." : `Manage all patients in the system. Total: ${patients.length} patients`}
              patients={patients.map((patient) => ({
                id: patient.id,
                profile_id: patient.profile_id,
                first_name: patient.first_name || patient.name?.split(' ')[0] || 'Unknown',
                last_name: patient.last_name || patient.name?.split(' ').slice(1).join(' ') || 'User',
                email: patient.email || '',
                phone: patient.phone,
                date_of_birth: patient.date_of_birth,
                has_completed_intake: patient.has_completed_intake,
                assigned_date: patient.assignedDate,
                is_primary: patient.isPrimary,
                assigned_providers: patient.assigned_providers,
                treatment_types: patient.treatment_types,
                medications: patient.medications,
                created_at: patient.created_at || new Date().toISOString(),
                // Include appointment data for previous/next visit columns
                previous_appointment: patient.previous_appointment,
                next_appointment: patient.next_appointment
              })) as any}
              loading={loading}
              showSearch={true}
              showAddButton={true}
              onAddPatient={handleAddPatient}
              onPatientClick={(patient: any) => {
                // Convert back to admin Patient type for the click handler
                const adminPatient: Patient = patients.find(p => p.id === patient.id) || patients[0]
                handlePatientClick(adminPatient)
              }}
              searchPlaceholder="Search patients by name, email, or phone..."
              addButtonLabel="Add New Patient"
              emptyStateText="No patients found. Add your first patient to get started."
            />
        </div>
      </SidebarInset>
      
      {/* Enhanced Patient Information Dialog */}
      <EnhancedPatientInformationDialog
        patient={selectedPatient ? {
          id: selectedPatient.id,
          profile_id: selectedPatient.profile_id,
          first_name: selectedPatient.first_name || selectedPatient.name.split(' ')[0] || '',
          last_name: selectedPatient.last_name || selectedPatient.name.split(' ').slice(1).join(' ') || '',
          email: selectedPatient.email || '',
          phone: selectedPatient.phone,
          date_of_birth: selectedPatient.date_of_birth,
          has_completed_intake: selectedPatient.has_completed_intake,
          treatment_types: selectedPatient.treatment_types || (selectedPatient.treatmentType ? [selectedPatient.treatmentType] : undefined),
          assigned_providers: selectedPatient.assigned_providers,
          medications: selectedPatient.medications,
          created_at: selectedPatient.created_at || new Date().toISOString()
        } : null}
        preferredMedications={patientPreferences}
        medicationOrders={patientOrders}
        appointments={patientAppointments}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        isAdmin={true}
        onRescheduleAppointment={handleRescheduleAppointment}
        onGetAvailableSlots={handleGetAvailableSlots}
        providers={providers}
        medicationPreferences={patientPreferences}
        initialSection="Information"
      />

      <AdminAccountDialog
        open={accountDialogOpen}
        onOpenChange={setAccountDialogOpen}
      />
      
      <SupportFeedbackDialog
        open={supportFeedbackDialogOpen}
        onOpenChange={setSupportFeedbackDialogOpen}
        type={supportFeedbackType}
        onSubmit={handleSupportFeedbackSubmit}
      />
    </SidebarProvider>
  )
}